import { useContext } from 'react';

import { ApolloError, gql, useApolloClient, useMutation } from '@apollo/client';

import { LocalizationContext } from 'localization';
import {
  ISendWakePacketRequest,
  ISendWakePacketResponse,
  ISendWakePacketsRequest,
  ISendWakePacketsResponse,
  SEND_WAKE_PACKET,
  SEND_WAKE_PACKETS,
} from 'schemas';
import notification from 'services/notificationService';

import { CREATE_COMMAND, CREATE_COMMANDS } from './schema';
import {
  TCommandAction,
  TSendCommand,
  ICreateCommandRequest,
  ICreateCommandsRequest,
  TSendCommands,
} from './types';

export const useSendShellCommand = (
  onSuccess?: CallableFunction
): {
  sendCommand: TSendCommand;
  createCommandError?: ApolloError;
  sendCommands: TSendCommands;
  createCommandsError?: ApolloError;
} => {
  const { t } = useContext(LocalizationContext);

  const NOTIFICATION_TITLES: Record<TCommandAction, string> = {
    ALLOW_ADMIN_MODE: t('host.notify.commands.highAccess'),
    DISABLE_SHELL: t('host.notify.commands.disable'),
    LOGOFF: t('host.notify.commands.logoff'),
    REBOOT: t('host.notify.commands.reboot'),
    SHUTDOWN: t('host.notify.commands.shutdown'),
    WOL: t('host.notify.commands.wol'),
    RUN: t('host.notify.commands.run'),
    SHOW_MESSAGE: t('host.notify.commands.message'),
    SEND_LOGS: t('host.notify.commands.logs'),
    SHELL_UPDATE: t('host.notify.commands.shellUpdate'),
  };

  const apolloClient = useApolloClient();

  const [createCommand, { error: createCommandError }] = useMutation<
    unknown,
    ICreateCommandRequest
  >(CREATE_COMMAND, {
    onCompleted: () => onSuccess?.(),
    onError: (error) => {
      if (error.message.includes('already exists')) {
        notification.info({
          t,
          type: 'error',
          title: t('host.notify.commands.alreadySend.title'),
          message: t('host.notify.commands.alreadySend.message'),
        });
      }
    },
  });

  const [createCommands, { error: createCommandsError }] = useMutation<
    unknown,
    ICreateCommandsRequest
  >(CREATE_COMMANDS, {
    onCompleted: () => onSuccess?.(),
    onError: (error) => {
      if (error.message.includes('already exists')) {
        notification.info({
          t,
          type: 'error',
          title: t('host.notify.commands.alreadySend.title'),
          message: t('host.notify.commands.alreadySend.message'),
        });
      }
    },
  });

  const [sendWakePacket] = useMutation<
    ISendWakePacketResponse,
    ISendWakePacketRequest
  >(SEND_WAKE_PACKET);

  const [sendWakePackets] = useMutation<
    ISendWakePacketsResponse,
    ISendWakePacketsRequest
  >(SEND_WAKE_PACKETS);

  const sendCommand: TSendCommand = ({ host_id, action, param }) => {
    const alias = apolloClient.readQuery({
      id: apolloClient.cache.identify({
        id: host_id,
        __typename: 'HostOverview',
      }),
      query: gql`
        query hostOverview {
          alias
        }
      `,
    })?.alias;

    return Promise.resolve()
      .then(() => {
        if (action === 'WOL') {
          return sendWakePacket({
            variables: {
              input: {
                host_id,
              },
            },
          }).then((response) => {
            if (!response.data?.sendWakePacket) {
              notification.info({
                t,
                type: 'error',
                title: NOTIFICATION_TITLES[action],
                message: t('host.notify.commands.wol.error'),
                leftDescription: alias,
                dismiss: 3,
              });

              return Promise.reject('signal aborted');
            }
          });
        }

        return createCommand({
          variables: {
            input: {
              host_id,
              action,
              ...(param ? { param } : {}),
            },
          },
        });
      })
      .then(() =>
        notification.info({
          t,
          type: 'warning',
          title: NOTIFICATION_TITLES[action],
          message: t('host.notify.commands.success'),
          leftDescription: alias,
          dismiss: 3,
        })
      )
      .catch(console.error);
  };

  const sendCommands: TSendCommands = ({ host_ids, action, param }) => {
    return Promise.resolve()
      .then(() => {
        if (action === 'WOL') {
          return sendWakePackets({ variables: { input: { host_ids } } });
        }

        return createCommands({
          variables: {
            input: {
              host_ids,
              action,
              ...(param ? { param } : {}),
            },
          },
        });
      })
      .then(() =>
        notification.info({
          t,
          type: 'warning',
          title: NOTIFICATION_TITLES[action],
          message: t('host.notify.commands.success'),
          dismiss: 3,
        })
      )
      .catch(console.error);
  };

  return { sendCommand, createCommandError, sendCommands, createCommandsError };
};
